import React, { useState } from 'react'
import { Icon } from '@iconify/react'
import {
  Dialog,
  DialogActions,
  MiniToggle,
  cleanSlugInput,
  isLinkUrl,
  slugify,
  useSlugCheck,
} from './parts'
import useStyles from './styles'

/** "learn.acme.com/" before a slug field; `host` has no protocol. */
const SlugField = ({ host, value, onChange, placeholder, invalid }) => {
  const classes = useStyles()
  return (
    <div
      className={classes.slugField}
      style={invalid ? { borderColor: '#ED4245' } : undefined}
    >
      <span className={classes.slugPrefix}>{host}/</span>
      <input
        className={classes.slugInput}
        value={value}
        placeholder={placeholder}
        onChange={event => onChange(cleanSlugInput(event.target.value))}
      />
    </div>
  )
}

/** Navigation › Add › New page (and "Create new page" from the footer). */
export const NewPageDialog = ({ host, showInMenu, onClose, onCreate }) => {
  const classes = useStyles()
  const [name, setName] = useState('')
  const [slugDraft, setSlugDraft] = useState(null)
  const [inMenu, setInMenu] = useState(showInMenu)
  const [saving, setSaving] = useState(false)
  const slug = slugDraft ?? slugify(name)
  const { checking, problem } = useSlugCheck(slug)
  const valid = Boolean(name.trim()) && Boolean(slug) && !problem && !checking

  const create = () => {
    if (!valid || saving) return
    setSaving(true)
    onCreate({ name: name.trim(), slug, showInMenu: inMenu }).finally(() =>
      setSaving(false),
    )
  }

  return (
    <Dialog
      title="New page"
      subtitle="You can change the name and URL later."
      onClose={onClose}
      actions={
        <DialogActions
          onCancel={onClose}
          onSubmit={create}
          submitLabel="Create and edit"
          submitDisabled={!valid || saving}
        />
      }
    >
      <label className={classes.label}>
        Page name
        <input
          className={classes.input}
          value={name}
          maxLength={100}
          placeholder="e.g. Contact"
          autoFocus
          onChange={event => setName(event.target.value)}
          onKeyDown={event => event.key === 'Enter' && create()}
        />
      </label>
      <div className={classes.label}>
        URL
        <SlugField
          host={host}
          value={slug}
          placeholder="contact"
          invalid={Boolean(problem)}
          onChange={setSlugDraft}
        />
        <span className={problem ? classes.helpError : classes.help}>
          {problem ||
            (slug ? `${host}/${slug}` : 'Fills in from the page name.')}
        </span>
      </div>
      <MiniToggle checked={inMenu} onChange={setInMenu}>
        Show in menu
      </MiniToggle>
    </Dialog>
  )
}

/** Add / edit an external link of the menu or the footer. */
export const LinkDialog = ({ link, subtitle, onClose, onSave, onRemove }) => {
  const classes = useStyles()
  const editing = Boolean(link?.id)
  const [label, setLabel] = useState(link?.label || '')
  const [url, setUrl] = useState(link?.url || 'https://')
  const [newTab, setNewTab] = useState(link ? link.newTab !== false : true)
  const [touched, setTouched] = useState(false)
  const urlOk = isLinkUrl(url)
  const valid = Boolean(label.trim()) && urlOk
  const bad = touched && url && !urlOk

  const save = () =>
    valid && onSave({ label: label.trim(), url: url.trim(), newTab })

  return (
    <Dialog
      title={editing ? 'Edit link' : 'Add external link'}
      subtitle={subtitle}
      onClose={onClose}
      actions={
        <DialogActions
          onRemove={editing ? onRemove : null}
          onCancel={onClose}
          onSubmit={save}
          submitLabel={editing ? 'Save' : 'Add link'}
          submitDisabled={!valid}
        />
      }
    >
      <label className={classes.label}>
        Label
        <input
          className={classes.input}
          value={label}
          maxLength={60}
          placeholder="e.g. Blog"
          autoFocus
          onChange={event => setLabel(event.target.value)}
        />
      </label>
      <label className={classes.label}>
        URL
        <input
          className={`${classes.inputMono} ${bad ? classes.inputError : ''}`}
          value={url}
          placeholder="https://acme.com/blog"
          onChange={event => {
            setUrl(event.target.value)
            setTouched(true)
          }}
          onKeyDown={event => event.key === 'Enter' && save()}
        />
        {bad && (
          <span className={classes.helpError}>
            Enter a full address starting with https:// or mailto:
          </span>
        )}
      </label>
      <MiniToggle checked={newTab} onChange={setNewTab}>
        Open in a new tab
      </MiniToggle>
    </Dialog>
  )
}

/**
 * Pages & menu › Change label. `urlPrefix` (e.g. "learn.acme.com/") shows the
 * address the new label gives; without it the address stays as it is.
 */
export const ChangeLabelDialog = ({
  title = 'Change label',
  label: initial,
  urlPrefix,
  urlFor,
  fixedUrl,
  onClose,
  onSave,
}) => {
  const classes = useStyles()
  const [label, setLabel] = useState(initial || '')
  const [saving, setSaving] = useState(false)
  const valid = Boolean(label.trim()) && label.trim() !== (initial || '').trim()
  const nextUrl = urlFor ? urlFor(label) : null

  const save = () => {
    if (!valid || saving) return
    setSaving(true)
    Promise.resolve(onSave(label.trim())).finally(() => setSaving(false))
  }

  return (
    <Dialog
      title={title}
      onClose={onClose}
      actions={
        <DialogActions
          onCancel={onClose}
          onSubmit={save}
          submitLabel={saving ? 'Saving…' : 'Save'}
          submitDisabled={!valid || saving}
        />
      }
    >
      <label className={classes.label}>
        Label
        <input
          className={classes.input}
          value={label}
          maxLength={60}
          autoFocus
          onChange={event => setLabel(event.target.value)}
          onKeyDown={event => event.key === 'Enter' && save()}
        />
        {urlPrefix && nextUrl && (
          <span className={classes.help}>
            URL: {urlPrefix}
            {nextUrl}
          </span>
        )}
        {fixedUrl && (
          <span className={classes.help}>The address stays {fixedUrl}.</span>
        )}
      </label>
    </Dialog>
  )
}

/** Footer › Add › Page: pick a portal page (or create one) and a label. */
export const FooterPageDialog = ({
  link,
  pages,
  onClose,
  onSave,
  onRemove,
  onNewPage,
}) => {
  const classes = useStyles()
  const editing = Boolean(link?.id)
  const fallback =
    pages.find(page => page.type === 'catalog')?.id || pages[0]?.id
  const [pageId, setPageId] = useState(link?.pageId || fallback)
  const selected = pages.find(page => page.id === pageId)
  const [label, setLabel] = useState(link?.label ?? null)
  const shownLabel = label ?? selected?.name ?? ''
  const valid = Boolean(selected) && Boolean(shownLabel.trim())

  return (
    <Dialog
      title={editing ? 'Edit footer link' : 'Add page link'}
      onClose={onClose}
      actions={
        <DialogActions
          onRemove={editing ? onRemove : null}
          onCancel={onClose}
          onSubmit={() => valid && onSave({ pageId, label: shownLabel.trim() })}
          submitLabel={editing ? 'Save' : 'Add link'}
          submitDisabled={!valid}
        />
      }
    >
      <div className={classes.label}>
        Page
        <div className={classes.pagePicker}>
          {pages.map(page => {
            const on = page.id === pageId
            return (
              <button
                key={page.id}
                type="button"
                className={`${classes.pageOption} ${
                  on ? classes.pageOptionOn : ''
                }`}
                onClick={() => {
                  // The label follows the page until it is edited.
                  if (label === null || label === selected?.name) setLabel(null)
                  setPageId(page.id)
                }}
              >
                <span
                  className={`${classes.radio} ${on ? classes.radioOn : ''}`}
                />
                <span className={classes.pageOptionName}>{page.name}</span>
                <span className={classes.pageOptionPath}>{page.path}</span>
              </button>
            )
          })}
          <button
            type="button"
            className={classes.newPageOption}
            onClick={onNewPage}
          >
            <Icon icon="tabler:plus" width={16} height={16} />
            Create new page
          </button>
        </div>
      </div>
      <label className={classes.label}>
        Label
        <input
          className={classes.input}
          value={shownLabel}
          maxLength={60}
          onChange={event => setLabel(event.target.value)}
        />
        <span className={classes.help}>Defaults to the page name.</span>
      </label>
    </Dialog>
  )
}
